import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../utils/axios';

interface QAReview {
  id: number;
  project_id: number;
  project_address: string;
  confidence_score: number;
  status: string;
  assigned_to: number | null;
  flagged_reason: string | null;
  priority: number;
  created_at: string;
}

interface QAStats {
  total_pending: number;
  total_in_review: number;
  total_completed: number;
  avg_review_time_hours: number;
  my_assigned: number;
  my_completed: number;
}

const AdminQA: React.FC = () => {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState<QAReview[]>([]);
  const [stats, setStats] = useState<QAStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showMyReviews, setShowMyReviews] = useState(false);

  useEffect(() => {
    fetchQAData();
  }, [statusFilter, showMyReviews]);

  const fetchQAData = async () => {
    try {
      const params: any = {};
      if (statusFilter) params.status = statusFilter;
      if (showMyReviews) params.assigned_to_me = true;

      const [reviewsRes, statsRes] = await Promise.all([
        axios.get('/api/qa/reviews', { params }),
        axios.get('/api/qa/dashboard/stats')
      ]);

      setReviews(reviewsRes.data);
      setStats(statsRes.data);
      setLoading(false);
    } catch (err: any) {
      if (err.response?.status === 403) {
        setError('Access denied. QA privileges required.');
      } else {
        setError('Failed to load QA reviews');
      }
      setLoading(false);
    }
  };

  const handleClaimReview = async (reviewId: number) => {
    try {
      await axios.post(`/api/qa/reviews/${reviewId}/claim`);
      fetchQAData();
    } catch (err) {
      alert('Failed to claim review');
    }
  };

  const handleViewReview = (reviewId: number) => {
    navigate(`/admin/qa/${reviewId}`);
  };

  const getStatusBadge = (status: string) => {
    const badges: any = {
      pending_human_review: 'badge-warning',
      in_human_review: 'badge-primary',
      pending_senior_review: 'badge-warning',
      in_senior_review: 'badge-primary',
      approved: 'badge-success',
      rejected: 'badge-error'
    };
    return badges[status] || 'badge-secondary';
  };

  const getStatusLabel = (status: string) => {
    return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getPriorityBadge = (priority: number) => {
    if (priority === 2) return { class: 'badge-error', label: 'Urgent' };
    if (priority === 1) return { class: 'badge-warning', label: 'High' };
    return { class: 'badge-secondary', label: 'Normal' };
  };

  if (loading) {
    return (
      <div className="loading-page">
        <p>Loading QA reviews...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <div className="container" style={{ paddingTop: '2rem' }}>
          <div className="alert alert-error">
            <span className="alert-icon">⚠️</span>
            <span>{error}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <div className="container">
          <div className="page-header-content">
            <div className="page-header-left">
              <button onClick={() => navigate('/admin')} className="btn btn-sm btn-ghost">
                ← Back
              </button>
              <h1 className="page-title">✅ QA Pipeline</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="page-content">
        <div className="container">
          {/* QA Stats */}
          {stats && (
            <div className="stats-grid" style={{ marginBottom: 'var(--spacing-6)' }}>
              <div className="stat-card">
                <div className="stat-label">Pending Review</div>
                <div className="stat-value">{stats.total_pending}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">In Review</div>
                <div className="stat-value">{stats.total_in_review}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Completed</div>
                <div className="stat-value">{stats.total_completed}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">My Assigned</div>
                <div className="stat-value">{stats.my_assigned}</div>
                <div className="stat-change" style={{ color: 'var(--color-gray-500)', fontSize: 'var(--text-sm)' }}>
                  {stats.my_completed} completed
                </div>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="card" style={{ marginBottom: 'var(--spacing-6)' }}>
            <div className="card-body">
              <div style={{ display: 'flex', gap: 'var(--spacing-4)', flexWrap: 'wrap', alignItems: 'center' }}>
                <select
                  className="input"
                  style={{ width: '250px' }}
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="">All Statuses</option>
                  <option value="pending_human_review">Pending Human Review</option>
                  <option value="in_human_review">In Human Review</option>
                  <option value="pending_senior_review">Pending Senior Review</option>
                  <option value="in_senior_review">In Senior Review</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
                <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={showMyReviews}
                    onChange={(e) => setShowMyReviews(e.target.checked)}
                  />
                  <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)' }}>
                    Show only my reviews
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Reviews Table */}
          <div className="card">
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Project</th>
                    <th>Confidence</th>
                    <th>Status</th>
                    <th>Priority</th>
                    <th>Flagged</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {reviews.length === 0 ? (
                    <tr>
                      <td colSpan={7}>
                        <div className="empty-state">
                          <div className="empty-state-title">No QA reviews found</div>
                          <div className="empty-state-description">
                            Reviews will appear here when analyses have low confidence scores
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    reviews.map((review) => {
                      const priorityBadge = getPriorityBadge(review.priority);
                      return (
                        <tr key={review.id}>
                          <td>
                            <div>
                              <div style={{ fontWeight: 'var(--font-semibold)', color: 'var(--color-gray-900)' }}>
                                #{review.project_id}
                              </div>
                              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)' }}>
                                {review.project_address || 'N/A'}
                              </div>
                            </div>
                          </td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                              <div style={{ 
                                width: '60px', 
                                height: '6px', 
                                background: 'var(--color-gray-200)', 
                                borderRadius: 'var(--radius-full)',
                                overflow: 'hidden'
                              }}>
                                <div style={{ 
                                  width: `${review.confidence_score * 100}%`, 
                                  height: '100%', 
                                  background: review.confidence_score < 0.5 ? 'var(--color-error)' : review.confidence_score < 0.7 ? 'var(--color-warning)' : 'var(--color-success)'
                                }} />
                              </div>
                              <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)' }}>
                                {(review.confidence_score * 100).toFixed(1)}%
                              </span>
                            </div>
                          </td>
                          <td>
                            <span className={`badge ${getStatusBadge(review.status)}`}>
                              {getStatusLabel(review.status)}
                            </span>
                          </td>
                          <td>
                            <span className={`badge ${priorityBadge.class}`}>
                              {priorityBadge.label}
                            </span>
                          </td>
                          <td style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)', maxWidth: '200px' }}>
                            {review.flagged_reason || 'Auto-flagged'}
                          </td>
                          <td style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)' }}>
                            {new Date(review.created_at).toLocaleDateString()}
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
                              {!review.assigned_to && review.status.includes('pending') && (
                                <button
                                  onClick={() => handleClaimReview(review.id)}
                                  className="btn btn-sm btn-primary"
                                >
                                  Claim
                                </button>
                              )}
                              <button
                                onClick={() => handleViewReview(review.id)}
                                className="btn btn-sm btn-outline"
                              >
                                View
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminQA;
